import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import {
  Paper,
  Box,
  Typography,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { StyledTypography } from "../../assets/css/styledComponent";
import { CREATE_TRANSACTION, SEND_EMAIL_MUTATION } from "../../graphql/mutations";
import { GET_PAYMENTS } from "../../graphql/queries";
import { UserContext } from "../../components/auth/auth-context";  

const theme = createTheme({
  palette: {
    primary: {
      main: "#d2b48c", // tan color
    },
    secondary: {
      main: "#8b4513", // saddle brown color
    },
  },
  typography: {
    fontFamily: "Arial",
  },
});

const NotFound = () => {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        height: "100%",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <div style={{ padding: "0px 20px" }}>
        <h1>This payment page is either your current one or invalid</h1>
        <p>The requested page could not be found.</p>
      </div>
    </div>
  );
};

const CustomerPaymentPage = (props) => { 
  const { businessUrl } = useParams()
  // Create a ref to hold the answers.
  const answersRef = useRef(props.answers);
  const emailRef = useRef(props.email);
  const { providerUser } = useContext(UserContext);
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  const [createTransaction, { loading: createTransactionLoading }] = useMutation(CREATE_TRANSACTION);

  const { data: paymentData, paymentLoading, error } = useQuery(GET_PAYMENTS, {
    variables: { businessUrl: businessUrl }
  });

  const paypalPayment = paymentData?.payments?.find(payment => payment.paymentMethod.type.toLowerCase() === 'paypal');
  const paypalEmail = paypalPayment ? paypalPayment.identifier : null;

  paymentLoading && <p>Loading ...</p>



  // Update the ref when props.answers changes.
  useEffect(() => {
    answersRef.current = props.answers;
    emailRef.current = props.email;
  }, [props.answers, props.email]);


  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [{
        description: `Booking for ${props?.bookingTypedata?.title}`,
        amount: {
          value: props?.bookingTypedata?.price, // Set the amount to be paid
        },
        payee: {
          email_address: paypalEmail, // Set the payee email
        },
      }],
    });

  } 
  const onApprove = (data, actions) => {
    return actions.order.capture().then(async function (details) { 
      createTransaction({
        variables: {
          input: {
            amount: parseFloat(details.purchase_units[0].amount.value),
            countryCode: details.payer.address.country_code,
            currency: details.purchase_units[0].amount.currency_code,
            description: details.purchase_units[0].description,
            payeeEmail: details.purchase_units[0].payee.email_address,
            transactionId: details.id,
            bookingTypeId: Number(props?.bookingTypedata?.id),
            businessId: Number(paypalPayment?.businessId),
            paymentDetails: JSON.stringify(details),
            payerId: Number(providerUser?.userId),
          }
        }
      }) 
      sendPaymentReceipt(details, sendEmail)
      props.onApprove(answersRef.current, emailRef.current);
    });
  };
 

  return (
    <ThemeProvider theme={theme}> 
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <Paper elevation={1} style={{ width: "95%", padding: 10 }}>
            <StyledTypography fontSize="30px">
              Complete your purchase
            </StyledTypography>
            <Typography variant="h6" gutterBottom>
              Item: {props?.bookingTypedata?.title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              Price: ${props?.bookingTypedata?.price}
            </Typography>


            {!paypalPayment ? (
              // Case 1: Payment method not integrated.
              <StyledTypography style={{ color: "red" }}>
                Payment method is not integrated.
                <br />
                Contact your provider.
              </StyledTypography>
            ) : !providerUser?.userId ? (
              // Case 2: Payment method integrated but user not logged in.
              <StyledTypography style={{ color: "red" }}>
                <Link to="/login" style={{ color: "red", textDecoration: "underline" }}>
                  Please login to continue
                </Link>
              </StyledTypography>
            ) : (
              // Case 3: Payment method integrated and user is logged in.
              <PayPalButtons
                key={props?.bookingTypedata?.title}
                style={{ layout: "vertical" }}
                createOrder={createOrder}
                onApprove={onApprove}
                disabled={props.disabled}
              />
            )}

          </Paper>

        </Box> 
    </ThemeProvider>

  );
};

/**
 * Sends a payment receipt email to both the payer and the payee.
 *
 * @param {object} details - The details object returned by PayPal after order capture.
 * @returns {Promise<void>}
 */
async function sendPaymentReceipt(details, sendSubscriptionEmail) {
  // Common subject for the receipt email.
  const emailSubject = "Payment Receipt";

  // Construct the email body for the payer.
  const payerEmailBody =
`Hello,

Thank you for your payment. Here are your transaction details:

Transaction ID: ${details.id}
Amount: ${details.purchase_units[0].amount.value} ${details.purchase_units[0].amount.currency_code}
Description: ${details.purchase_units[0].description}

We appreciate your business.

Thank you,
Your Team`;

  // Construct the email body for the payee.
  const payeeEmailBody =
`Hello,

You have received a payment. Here are the transaction details:

Transaction ID: ${details.id}
Amount: ${details.purchase_units[0].amount.value} ${details.purchase_units[0].amount.currency_code}
Description: ${details.purchase_units[0].description}

Thank you,
Your Team`;

  // Send receipt email to the payer.
  try {
    await sendSubscriptionEmail({
      variables: {
        to: details.payer.email_address,
        subject: emailSubject,
        text: payerEmailBody,
      },
    }); 
  } catch (error) {
    console.error("Error sending receipt email to payer:", error);
  }

  // Send receipt email to the payee.
  try {
    await sendSubscriptionEmail({
      variables: {
        to: details.purchase_units[0].payee.email_address,
        subject: emailSubject,
        text: payeeEmailBody,
      },
    }); 
  } catch (error) {
    console.error("Error sending receipt email to payee:", error);
  }
}


export default CustomerPaymentPage;